import moment from "moment";
import React from "react";
import {
	TableSortLabel,
	TableContainer,
	TableCell,
	TableHead,
	TableRow,
	TableBody,
	Table,
	Paper,
	Box,
	Checkbox,
	Typography,
	Button,
} from "@mui/material";

import { HeadCell, headCells, headCellsWithoutHospital, Row } from "./TableCustomUtils";
import DropdownIcon from "../../../assets/icons/filter_dropdown_icon.svg";
import NursingFunctionsNurse from "../../../assets/icons/nursing-functions-nurse.svg";
import { useNursingFunctionsContext } from "../NursingFunctionsProvider";
import { Position } from "../../../views/Login/LoginUtils";
import { LoggedUser } from "../../../store/interfaces";
import { AppState } from "../../../store/reducer";
import { useSelector } from "react-redux";
import { Plus } from "lucide-react";

const customIcon = () => {
	return (
		<Box className="sort-icon">
			<img
				width={10}
				src={DropdownIcon}
			/>
		</Box>
	);
};

function TableHeaders() {
	const { onChangeSort, order, orderBy, numSelected, rowCount, onSelectAll } =
		useNursingFunctionsContext();

	const createSortHandler = (property: keyof Row) => (event: React.MouseEvent<unknown>) => {
		onChangeSort(event, property);
	};

	const loggedUser: LoggedUser = useSelector((state: AppState) => state.loggedUser);

	const headColumns: HeadCell[] =
		loggedUser.position === Position.SUPER ? [...headCells] : [...headCellsWithoutHospital];

	return (
		<TableHead className="table__header">
			<TableRow>
				<TableCell padding="checkbox">
					<Checkbox
						className="table__checkbox"
						indeterminate={numSelected > 0 && numSelected < rowCount}
						checked={rowCount > 0 && numSelected === rowCount}
						onChange={onSelectAll}
					/>
				</TableCell>
				{headColumns.map((headCell: HeadCell) => {
					return (
						<TableCell
							key={headCell.id}
							sx={{
								width: `${headCell.width}rem !important`,
							}}
							className="table__header__option"
							sortDirection={orderBy === headCell.id ? order : false}
						>
							<TableSortLabel
								active={orderBy === headCell.id}
								direction={orderBy === headCell.id ? order : "asc"}
								onClick={createSortHandler(headCell.id)}
								IconComponent={customIcon}
							>
								{headCell.label}
							</TableSortLabel>
						</TableCell>
					);
				})}
			</TableRow>
		</TableHead>
	);
}

function TableContent() {
	const { rows, selectedRows, onSelect } = useNursingFunctionsContext();

	const loggedUser: LoggedUser = useSelector((state: AppState) => state.loggedUser);

	return (
		<TableBody>
			{rows.map((row: Row, index: number) => {
				const isItemSelected: boolean = selectedRows.includes(row.id);

				return (
					<TableRow
						hover
						key={`row-${row.id}`}
						className="table__row"
						style={{
							height: "4rem",
							cursor: "pointer",
						}}
						onClick={() => onSelect(row.id)}
					>
						<TableCell padding="checkbox">
							{" "}
							<Checkbox
								className="table__checkbox"
								checked={isItemSelected}
							/>
						</TableCell>
						<TableCell className="table__cell">{row.nurseName}</TableCell>
						{loggedUser.position === Position.SUPER && (
							<TableCell className="table__cell">{row.hospitalName}</TableCell>
						)}
						<TableCell className="table__cell">{row.areaName}</TableCell>
						<TableCell className="table__cell table__cell--highlight">
							{row.functionName}
						</TableCell>
						<TableCell className="table__cell">
							{moment(row.date).format("DD/MM/YYYY")}
						</TableCell>
					</TableRow>
				);
			})}
		</TableBody>
	);
}

export default function TableCustom() {
	const { isLoading, rows, setShowModal } = useNursingFunctionsContext();

	return (
		<Box className="table-container">
			{rows.length > 0 ? (
				<TableContainer
					component={Paper}
					className="table"
				>
					<Table
						size="small"
						stickyHeader
						sx={{
							tableLayout: "auto",
						}}
					>
						<TableHeaders />
						<TableContent />
					</Table>
				</TableContainer>
			) : (
				<Box className="table table--empty">
					{isLoading ? (
						<Box>
							<Box className="custom-loader"></Box>
						</Box>
					) : (
						<>
							<img src={NursingFunctionsNurse} />
							<Typography className="table--empty__title">
								Aún no hay funciones asignadas.
							</Typography>
							<Typography className="table--empty__subtitle">
								Aquí podrás ver y asignar funciones específicas al personal de
								enfermería. <br />
								Comienza seleccionando un profesional.
							</Typography>

							<Button
								className="table--empty__button"
								onClick={() => setShowModal(true)}
							>
								<Plus />
								Asignar función
							</Button>
						</>
					)}
				</Box>
			)}
		</Box>
	);
}
